/* eslint-disable @typescript-eslint/no-unused-expressions */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { DebugPlugin, gsapLog as debugLogFromDebugPlugin } from './gsap-debug-plugin';

const logPrefix = '⛔️ abort ⛔️';
const signalEventListenerOptions = { passive: true, capture: true };

const config = {
  logAbort: false,
  logMultipleInitialize: false,
  logRemoveListener: false,
};

const debugLog = (animation, prefix, message) => {
  if (config.logAbort) debugLogFromDebugPlugin(animation, prefix, message);
};

export const AbortPlugin = {
  name: 'abort',
  version: '0.0.0',
  headless: true,
  register(core) {
    if (config.logAbort) core.registerPlugin(DebugPlugin);
    console.log('🎬 Registering Gsap ⛔️ Abort Plugin ⛔️');
    decorateGsap(core);
  },
  init(target, vars, tween) {
    if (!vars.signal) throw new Error('AbortPlugin requires a signal');
    const signal = vars.signal;
    const revert = vars.revert !== undefined && vars.revert;
    initializeAbort(tween, signal, revert);
  },
};

const decorateGsap = (gsap) => {
  const originalTimeline = gsap.timeline;
  gsap.timeline = function (vars) {
    return initializeAbort(originalTimeline(vars));
  };

  const originalTo = gsap.to;
  gsap.to = function (target, vars) {
    return initializeAbort(originalTo(target, vars));
  };

  const originalFrom = gsap.from;
  gsap.from = function (target, vars) {
    return initializeAbort(originalFrom(target, vars));
  };

  const originalFromTo = gsap.fromTo;
  gsap.fromTo = function (target, fromVars, toVars) {
    if ('abort' in fromVars && 'abort' in toVars)
      throw new Error('⛔️ AbortPlugin provided in both fromVars and toVars ⛔️');
    if ('abort' in fromVars) toVars.abort = fromVars.abort;
    return initializeAbort(originalFromTo(target, fromVars, toVars));
  };

  const originalThen = gsap.core.Animation.prototype.then;
  gsap.core.Animation.prototype.then = function (onFulfilled, onReject) {
    switch (true) {
      case this.vars.abort !== undefined:
        if (!this.vars.abort.signal) throw new Error('AbortPlugin requires a signal');
        if (!this.vars.abort.initialized) throw new Error('AbortPlugin requires a signal');

        return new Promise(() => {
          const signal = this.vars.abort.signal;
          const needsRevert = this.vars.abort.revert !== undefined && this.vars.abort.revert;
          const clearListener = () => signal.removeEventListener('abort', onAbortHandler, signalEventListenerOptions);
          const onAbortHandler = () => {
            clearListener();
            if (needsRevert) this.revert();
            this.kill();
            onReject && onReject(signal.reason);
          };
          originalThen.call(this, () => {
            clearListener();
            onFulfilled && onFulfilled();
          });

          switch (signal.aborted) {
            case true:
              onAbortHandler();
              break;
            case false:
              signal.addEventListener('abort', onAbortHandler, signalEventListenerOptions);
              break;
          }
        });
      default:
        return originalThen.call(this, onFulfilled, onReject);
    }
  };
};

function initializeAbort(animation) {
  if (!animation.vars.abort) return animation;
  if (!animation.vars.abort.signal) throw new Error('⛔️ AbortPlugin requires an abort signal ⛔️');
  if (animation.vars.abort.initialized) {
    if (config.logMultipleInitialize) debugLog(animation, logPrefix, 'already initialized');
    return animation;
  }

  animation.vars.abort.initialized = true;

  const signal = animation.vars.abort.signal;
  const revert = animation.vars.abort.revert !== undefined && animation.vars.abort.revert;

  debugLog(animation, logPrefix, 'init');

  let listeningEvent = false;
  const addEventListener = () => {
    listeningEvent = true;
    signal.addEventListener('abort', onAbort, signalEventListenerOptions);
  };
  const removeEventListener = () => {
    if (!listeningEvent) return;
    listeningEvent = false;
    if (config.logRemoveListener) debugLog(animation, logPrefix, `remove listener`);
    signal.removeEventListener('abort', onAbort, signalEventListenerOptions);
  };

  const onAbort = (event, already) => {
    debugLog(animation, logPrefix, `🔥 aborted 🔥${already === true ? ' already:' : ':'} ${signal.reason}`);
    removeEventListener();
    if (revert) animation.revert();
    animation.kill();
  };

  const onInterrupt = animation.vars.onInterrupt;
  animation.vars.onInterrupt = (...vars) => {
    debugLog(animation, logPrefix, 'interrupted');
    removeEventListener();
    onInterrupt && onInterrupt(...vars);
  };

  const onComplete = animation.vars.onComplete;
  animation.vars.onComplete = (...vars) => {
    debugLog(animation, logPrefix, 'completed');
    removeEventListener();
    onComplete && onComplete(...vars);
  };

  if (signal.aborted) onAbort(null, true);
  else addEventListener();

  return animation;
}

export { AbortPlugin as default };
